import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import {
  FocusContext,
  init,
  pause,
  resume,
  setFocus,
  updateAllLayouts,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import Splash from 'components/loading/Splash'
import { SMOOTH_DURATION } from 'libs/constants/delay'
import KEYCODE from 'libs/constants/keycode'
import { RootState } from 'libs/store'
import {
  REQ_GET_EVENT_BROADCAST_LIST,
  REQ_GET_PARTNER_LIST,
  REQ_GET_RECENT_BROADCAST_LIST,
  REQ_GET_SCHEDULE_LIST,
} from 'libs/store/app/app.store'
import { SET_FROM_POSITION, SET_NOW_POSITION } from 'libs/store/interaction/interaction.store'
import { getFocusKey, scrollToSmooth } from 'libs/utils'
import BrandListContainer from './Brand.container'
import CloseConfirm from './CloseConfirm'
import CustomCenter from './CustomCenter'
import EventBroadcastContainer from './EventBroadcast.container'
import NextBroadcastListContainer from './NextBroadcast.container'
import RecentBroadcastContainer from './RecentBroadcast.container'
import SettingContainer from './Setting.container'

type Props = {
  renderPartnerId: string | null
}
const HomeContainer: React.FC<Props> = ({ renderPartnerId }) => {
  const isBroadcastLoading = useSelector((state: RootState) => state.app.isBroadcastLoading)
  const isScheduleLoading = useSelector((state: RootState) => state.app.isScheduleLoading)
  const showEventLoading = useSelector((state: RootState) => state.app.isEventLoading)
  const lastPositionInfo = useSelector((state: RootState) => state.interaction.nowPosition)
  const eventSectionSetting = useSelector((state: RootState) => state.app.eventSectionSetting)

  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState<boolean>(false)
  const [isOpenCustomCenterModal, setIsOpenCustomCenterModal] = useState<boolean>(false)
  const [isOpenCloseModal, setIsOpenCloseModal] = useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  const isLoading =
    isBroadcastLoading || isScheduleLoading || (renderPartnerId ? false : showEventLoading)

  const { ref, focusKey } = useFocusable({ focusKey: 'home' })
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const THROTTLE_MS = 200

  init({
    throttle: THROTTLE_MS,
    throttleKeypresses: true,
  })

  /**
   * 첫 로딩 시에만 전체 방송과 편성표 초기화 useEffect
   */
  useEffect(() => {
    if (!isLoading) {
      setIsInitialLoading(false)
      return
    }

    if (!renderPartnerId) dispatch(REQ_GET_EVENT_BROADCAST_LIST())

    dispatch(REQ_GET_PARTNER_LIST())
    dispatch(REQ_GET_RECENT_BROADCAST_LIST({ renderPartnerId }))
    dispatch(REQ_GET_SCHEDULE_LIST({ renderPartnerId }))
  }, [isLoading, dispatch, renderPartnerId])

  /**
   *  home화면 진입시 포커싱 초기화 useEffect
   */
  useLayoutEffect(() => {
    if (isLoading) return
    dispatch(
      SET_FROM_POSITION({
        page: '',
        section: '',
        broadcastId: '',
        partnerId: '',
        index: 0,
      }),
    )
    if (!lastPositionInfo.partnerId) {
      updateAllLayouts()
      if (eventSectionSetting.showEventSection) return setFocus('event')
      return setFocus('recent')
    }

    const focusKey = getFocusKey(lastPositionInfo)
    setFocus(focusKey)
    return
  }, [dispatch, isLoading, lastPositionInfo, eventSectionSetting])

  useEffect(() => {
    const isShowModal = isOpenCloseModal || isOpenCustomCenterModal
    isShowModal ? pause() : resume()
  }, [isOpenCloseModal, isOpenCustomCenterModal])

  /**
   * enter 눌렀을 때 이벤트 매핑
   */
  const onPressEnter = useCallback(
    (option) => {
      const { section, broadcastId, partnerId, index } = option
      const positionInfo = {
        page: 'home',
        partnerId,
        broadcastId,
        section,
        index,
      }

      if (partnerId) {
        dispatch(SET_NOW_POSITION(positionInfo))
      }

      switch (section) {
        case 'setting-language':
          setIsLanguageSelectorOpen(!isLanguageSelectorOpen)
          break
        case 'setting-customer':
          setIsOpenCustomCenterModal(!isOpenCustomCenterModal)
          break
        case 'event':
          navigate({
            pathname: '/player',
            search: `?broadcastId=${broadcastId}&partnerId=${partnerId}&fc=saucetv`,
          })
          break
        case 'recent':
          navigate({
            pathname: '/player',
            search: `?broadcastId=${broadcastId}&partnerId=${partnerId}&fc=saucetv`,
          })
          break
        case 'next':
          if (partnerId === 'more') {
            navigate({
              pathname: '/schedule',
            })
          } else {
            navigate({
              pathname: '/player',
              search: `?broadcastId=${broadcastId}&partnerId=${partnerId}&fc=saucetv`,
            })
          }
          break
        case 'brand':
          navigate({
            pathname: '/partners',
            search: `?partnerId=${partnerId}`,
          })
          break
      }
    },
    [isLanguageSelectorOpen, isOpenCustomCenterModal, navigate, dispatch],
  )

  const onFocusCard = (layout, extraProps, details) => {
    const { y } = layout
    const { event } = details

    // 페이지 이동 직후 instant scroll 사용
    if (!event) {
      scrollTo({ top: y, behavior: 'instant' })
      return
    }

    // 그 외의 경우 smooth scroll 사용
    scrollToSmooth(y, SMOOTH_DURATION)
  }

  const onStopSplash = () => {
    setIsInitialLoading(false)
  }

  /**
   * 키다운 이벤트 매핑 useEffect
   */
  useEffect(() => {
    const handleKeyDownPartnerContent = (e: KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (e.keyCode === KEYCODE.BACK || e.keyCode === KEYCODE.BACK_SPACE) {
        if (isLanguageSelectorOpen) {
          setIsLanguageSelectorOpen(false)
          return
        }
        if (isOpenCustomCenterModal) {
          setIsOpenCustomCenterModal(false)
          return
        }
        setIsOpenCloseModal(!isOpenCloseModal)
      }
    }

    window.addEventListener('keydown', handleKeyDownPartnerContent)
    return () => {
      window.removeEventListener('keydown', handleKeyDownPartnerContent)
    }
  }, [dispatch, isLanguageSelectorOpen, isOpenCloseModal, isOpenCustomCenterModal, navigate])

  return (
    <FocusContext.Provider value={focusKey}>
      <HomeContainerWrapper ref={ref}>
        <SettingContainer
          isLanguageSelectorOpen={isLanguageSelectorOpen}
          setIsLanguageSelectorOpen={setIsLanguageSelectorOpen}
          onPressEnter={onPressEnter}
        />
        <ScrollableRowContainer>
          {isLoading && (
            <Splash
              isShowSplash={location.state?.from ? false : isInitialLoading}
              onPause={onStopSplash}
            />
          )}
          <>
            {!isLoading && (
              <>
                {eventSectionSetting.showEventSection && !renderPartnerId && (
                  <EventBroadcastContainer onPressEnter={onPressEnter} onFocus={onFocusCard} />
                )}
                <RecentBroadcastContainer onPressEnter={onPressEnter} onFocus={onFocusCard} />
                <NextBroadcastListContainer onPressEnter={onPressEnter} onFocus={onFocusCard} />
                {!renderPartnerId && (
                  <BrandListContainer onPressEnter={onPressEnter} onFocus={onFocusCard} />
                )}
              </>
            )}
            <>
              {isOpenCloseModal && (
                <CloseConfirm
                  renderPartnerId={renderPartnerId}
                  isOpenCloseModal={isOpenCloseModal}
                  setIsOpenCloseModal={setIsOpenCloseModal}
                />
              )}

              {isOpenCustomCenterModal && (
                <CustomCenter
                  isOpenCustomCenterModal={isOpenCustomCenterModal}
                  setIsOpenCustomCenterModal={setIsOpenCustomCenterModal}
                />
              )}
            </>
          </>
        </ScrollableRowContainer>
      </HomeContainerWrapper>
    </FocusContext.Provider>
  )
}

const HomeContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  overflow: hidden;
  padding: 40px;
  background-color: #16161a;
`

const ScrollableRowContainer = styled.div`
  display: flex;
  flex-direction: column;
`

export default HomeContainer
